// src/hooks/useTransactionTimeline.ts
// Hook for fetching transaction timeline/events
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TimelineEvent {
  id: string;
  event_type: string;
  timestamp: string;
  status: string;
  description: string;
  metadata: Record<string, unknown>;
}

export interface TransactionTimeline {
  success: boolean;
  transaction_id: string;
  events: TimelineEvent[];
  current_status: string;
  created_at: string;
  settled_at: string | null;
}

export function useTransactionTimeline(txId: string | null) {
  return useQuery({
    queryKey: ['transaction_timeline', txId],
    queryFn: async () => {
      if (!txId) return null;

      const { data, error } = await supabase.rpc('user_transaction_timeline', {
        p_tx_id: txId,
      });

      if (error) {
        throw new Error(error.message || 'Failed to load transaction timeline');
      }

      return data as TransactionTimeline | null;
    },
    enabled: !!txId,
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error: any) => {
      // Don't retry on 401/403/404
      if (error?.code && ['401', '403', '404', 'PGRST001'].includes(error.code)) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

